<button
  type="button"
  class="pitchin-icon pitchin-icon-close tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
  [attr.aria-label]="'common.close' | translate"
  (click)="close()"
></button>

<div class="modal-body tw-text-black" [class.is-loading]="isLoading">
  <!-- Header: "Order Summary" title with the ticker code as a subtitle (Figma 177-2). -->
  <div class="tw-mb-4 tw-flex tw-items-start tw-gap-2">
    <div>
      <div class="order-summary-title">{{ 'mysec.orderSummary' | translate }}</div>
      <div class="order-summary-ticker">
        {{ transaction.orderSummary?.tickerCode | uppercase }}
      </div>
    </div>
    <span
      *ngIf="transaction.share?.isSandbox ?? transaction.company?.isSandbox"
      class="tw-ml-1 tw-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-secondary-light tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary-light"
    >
      {{ 'common.sandbox' | translate }}
    </span>
  </div>

  <ng-container *ngIf="transaction.orderSummary; else orderSummaryUnavailable">
    <!-- Buy/Sell badge (left) + Transaction ID copy pill (right) -->
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <span
        *ngIf="isBuy !== null"
        class="order-type-badge p-skeleton-bar"
        [ngClass]="isBuy ? 'order-type-badge--buy' : 'order-type-badge--sell'"
      >
        PSTX {{ (isBuy ? 'mysec.buy' : 'mysec.sell') | translate | uppercase }}
      </span>
      <button
        type="button"
        class="txn-id-btn p-skeleton-bar"
        (click)="copyTransactionId()"
      >
        <img
          class="txn-id-btn__icon"
          src="assets/img/icons/common/clipboard_icon_grey@2x.png"
          alt=""
        />
        {{ 'mysec.transactionId' | translate }}
      </button>
    </div>

    <!-- Matched-order table card -->
    <div class="summary-card tw-mt-4">
      <div class="table--row summary-card__head tw-grid tw-grid-cols-5">
        <div>{{ 'mysec.shares' | translate }}</div>
        <div class="tw-col-span-2 tw-text-right">{{ 'mysec.pricePerShareShort' | translate }}</div>
        <div class="tw-col-span-2 tw-text-right">{{ 'mysec.amount' | translate }}</div>
      </div>

      <div
        class="table--row summary-card__row tw-grid tw-grid-cols-5"
        *ngFor="let item of transaction?.orderSummary?.orderDetail; index as i"
      >
        <div class="p-skeleton-bar tw-tabular-nums">{{ item.quantity }}</div>
        <div class="p-skeleton-bar tw-col-span-2 tw-text-right tw-tabular-nums tw-whitespace-nowrap">
          {{ item.pricePerShare | numberSuffix: { numberFormat: '1.2-2' } }}
        </div>
        <div class="p-skeleton-bar tw-col-span-2 tw-text-right tw-font-semibold tw-tabular-nums tw-whitespace-nowrap">
          <ng-container *ngTemplateOutlet="amountValue; context: { amount: item.amount }"></ng-container>
        </div>
      </div>
    </div>

    <!-- Totals: Shares Value + Trading Fee grouped in one shaded box; Total
         pulled out into a separate divided row (attachment 2). -->
    <div class="summary-card summary-card--totals tw-mt-3">
      <div class="table--row summary-card__row tw-grid tw-grid-cols-5">
        <div class="summary-card__label tw-col-span-3 tw-font-medium">
          {{ 'mysec.sharesValue' | translate }}
        </div>
        <div class="tw-col-span-2 tw-text-right tw-font-semibold tw-tabular-nums tw-whitespace-nowrap">
          <ng-container *ngTemplateOutlet="amountValue; context: { amount: sharesValue }"></ng-container>
        </div>
      </div>

      <div class="table--row summary-card__row tw-grid tw-grid-cols-5">
        <div class="summary-card__label tw-col-span-3 tw-font-medium">
          {{ 'mysec.tradingFee' | translate }}
        </div>
        <div class="summary-card__label tw-col-span-2 tw-text-right tw-tabular-nums tw-whitespace-nowrap">
          - {{ transaction.orderSummary?.tradingFee | numberSuffix: { numberFormat: '1.2-2' } }}
        </div>
      </div>
    </div>

    <div class="summary-total table--row tw-mt-3 tw-grid tw-grid-cols-5 tw-items-center">
      <div class="tw-col-span-3 tw-font-bold">{{ 'common.total' | translate }}</div>
      <div
        class="tw-col-span-2 tw-text-right tw-font-bold tw-tabular-nums tw-whitespace-nowrap"
        [ngClass]="isBuy ? 'tw-text-primary' : 'tw-text-secondary-light'"
      >
        <ng-container
          *ngTemplateOutlet="
            amountValue;
            context: { amount: transaction.walletTransactionHistory?.amount }
          "
        ></ng-container>
      </div>
    </div>

    <!-- View full order in PSTX Orders page -->
    <div class="tw-mt-4 tw-text-center">
      <a
        [routerLink]="['/dashboard/orders']"
        [queryParams]="{ tab: 'history', historyFilter: 'successful' }"
        (click)="close()"
        class="tw-font-semibold tw-text-secondary-light tw-underline hover:tw-underline"
      >
        {{ 'mysec.viewFullOrderInPstx' | translate }}
      </a>
    </div>
  </ng-container>

  <!-- Graceful empty state: a settled trade row whose order detail can't be resolved
       (e.g. the underlying order/contract is no longer available) renders this
       instead of a blank modal body. -->
  <ng-template #orderSummaryUnavailable>
    <div class="tw-py-8 tw-text-center tw-text-body">
      {{ 'mysec.orderSummaryUnavailable' | translate }}
    </div>
  </ng-template>
</div>

<!-- Amount cell: Figma shows a single sign per popup — "-" for a Buy, "+" for a Sell —
     in front of the absolute value. Line amounts inherit the default (black); only the
     Total row is coloured by its parent. -->
<ng-template #amountValue let-amount="amount">
  <ng-container *ngIf="amount != null">
    {{ isBuy ? '-' : '+' }}
    {{ (amount < 0 ? -amount : amount) | numberSuffix: { numberFormat: '1.2-2' } }}
  </ng-container>
</ng-template>
